import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ResolvedUvInserts } from '@shared/view/uv-inserts/resolve-uv-inserts';
import {
  type UVInsertConfig,
  getUvInsertTextListKeys,
  isUvInsertDerivedElement,
  isUvInsertTextListElement,
  isUvInsertUserConfigurableText
} from '@shared/models/uv-inserts';
import {
  getFaceElementsForRegionTab,
  getSuggestionTargetTextPropertyKeyForRegionTab,
  getImageUploadPropertyKeysForRegionTab,
  getUvInsertFaceTabItems
} from '@shared/view/uv-inserts/visualiser-tabs';
import { getLiquidProductVariantImageSrc } from '@shared/view/uv-inserts/liquid-variant-image';
import {
  cropImageToView,
  getCroppedImageOrigin,
  IDENTITY_IMAGE_TRANSFORM,
  isCroppedImage,
  parseCssPixels,
  rememberCroppedImage,
  type ImageTransform
} from '@uv-inserts/utils/cropImageToView';
import { hasCardPreviewContent, renderCardPreview } from '@uv-inserts/utils/renderCardPreview';

/** Stable empty default so an absent `derivedTextByKey` never re-triggers the merge effect. */
const EMPTY_DERIVED_TEXT: Record<string, string> = {};

/** Both row-count variants of the Story of Us keepsake card share every card-only behaviour below. */
export const STORY_OF_US_CARD_HANDLES = ['story-of-us-card', 'story-of-us-card-7'] as const;

/**
 * Config handles whose framed photo uploads are rasterised to the crop the customer framed before
 * the file reaches the cart. Every other insert sends the customer's original upload, exactly as it
 * did before the crop feature existed.
 *
 * Why this is an allowlist rather than a behaviour for all inserts: cropping changes the artwork the
 * print partner receives. On an existing insert a customer who pans or zooms would stop sending
 * their original file and start sending a re-encoded JPEG, which is a change to live products' print
 * files that Pulse/Codeture have not signed off. The new Story of Us card has never been printed, so
 * it can crop today; the rest stay unchanged until that sign-off lands, at which point handles are
 * added here.
 */
export const UV_INSERT_CROP_TO_FRAME_HANDLES: readonly string[] = STORY_OF_US_CARD_HANDLES;

/**
 * Config handles whose assembled face is rasterised to an image and sent to the cart, so the
 * customer (and support, on the order) can see the card they built.
 *
 * An allowlist for the same reason the crop one is: the image rides into the base line's properties
 * as a new customer-visible line, and no existing insert has been signed off to start showing one.
 * Only the new Story of Us card, which has never shipped, opts in today.
 */
export const UV_INSERT_CARD_PREVIEW_HANDLES: readonly string[] = STORY_OF_US_CARD_HANDLES;

/**
 * Config handles whose editable face is rendered as a slide inside the piece visualiser at the top
 * of the modal, instead of inline in the step body.
 *
 * An allowlist like the two above: the face moving into the carousel changes the shape of the step
 * for anything listed here, and only the keepsake card was designed for it (its step also carries
 * the photo and text fields below it). Every other insert renders exactly where it always has.
 */
export const UV_INSERT_CARD_SLIDE_HANDLES: readonly string[] = STORY_OF_US_CARD_HANDLES;

/** The tab label the piece visualiser shows for that slide. */
export const UV_INSERT_CARD_SLIDE_LABEL = 'Keepsake Card';

/**
 * The line item property the rasterised card is sent under — and the label the cart shows beside it.
 *
 * It deliberately does NOT start with `UV`: `cart-item-row.liquid` skips every `UV`-prefixed
 * property when rendering CDN-hosted images as thumbnails (`property_first_two_chars != 'UV'`), and
 * every other card property starts with `UV` (`UVStory...`, `UVFrontImage`, `UVFrontText`).
 * Renaming this key to start with `UV` hides the image from the cart.
 */
export const UV_INSERT_CARD_PREVIEW_KEY = 'Keepsake card';

/** Stable empty map so a non-cropping config never re-triggers the crop effect or `isCropPending`. */
const NO_IMAGE_WINDOW_SIZES: Record<string, { width: number; height: number }> = {};

/** How long the pan/zoom must settle before we rasterise, so dragging does not thrash the canvas. */
const CROP_DEBOUNCE_MS = 300;

/** How long typing must settle before the card is redrawn — a full face costs more than a crop. */
const CARD_PREVIEW_DEBOUNCE_MS = 400;

/** A rasterised crop, tagged with the file and transform it was produced from. */
type CroppedImageEntry = {
  source: File;
  transform: ImageTransform;
  file: File;
};

/** A crop that could not be produced, tagged with the file and transform it was attempted for. */
type CropFailure = {
  source: File;
  transform: ImageTransform;
};

/**
 * The rasterised card, tagged with the image inputs it was drawn from.
 *
 * The images are held in state rather than only in `cardPreviewInputsRef` because the merge below
 * has to react to them: a ref mutation does not re-render, so a preview withheld as stale could
 * stay withheld after the redraw landed.
 */
type CardPreviewState = {
  file: File;
  imageByKey: Record<string, File | null>;
  variantImageUrl: string | null;
};

/**
 * The CSS pixel size of every IMAGE_UPLOAD window on a config, keyed by line item property key.
 *
 * Only windows sized in pixels can be cropped without measuring the DOM; percentage or keyword
 * sizes are omitted and those uploads are sent uncropped.
 *
 * @param options - Options object.
 * @param options.config - The active UV insert config.
 * @returns The pixel window size per image key.
 */
function getImageWindowSizes({ config }: { config: UVInsertConfig | null | undefined }): Record<
  string,
  { width: number; height: number }
> {
  const sizes: Record<string, { width: number; height: number }> = {};
  if (!config) return sizes;

  for (const face of config.faceElements) {
    for (const element of face.elementConfig) {
      if (element.type !== 'IMAGE_UPLOAD') continue;
      const key = element.lineItemPropertyKey?.trim();
      if (!key) continue;
      // Mirrors the '200px' default `index.jsx` passes to VisualiserImageArea.
      const width = parseCssPixels(element.size?.width ?? '200px');
      const height = parseCssPixels(element.size?.height ?? '200px');
      if (width == null || height == null) continue;
      sizes[key] = { width, height };
    }
  }

  return sizes;
}

/** The three image maps the VM starts with, rebuilt from a prior selection. */
type RestoredImageState = {
  imageByKey: Record<string, File | null>;
  imageTransformByKey: Record<string, ImageTransform>;
  croppedImageByKey: Record<string, CroppedImageEntry>;
};

/**
 * Rebuilds the VM's image state from the selection held in context.
 *
 * The selection carries only what the cart needs, so for a framed photo that is the *crop*, not the
 * customer's upload. Restoring it verbatim would strand the original: the customer could no longer
 * zoom out to recover what they had cropped away, and a re-frame would crop the already-upscaled
 * raster again. `getCroppedImageOrigin` maps a crop we produced back to its source and framing, so a
 * remount restores the original photo, the pan/zoom the customer left it at, and the crop itself —
 * which means the cart keeps the same file and nothing is rasterised twice.
 *
 * Any file we did not produce (a plain upload, or a crop from an earlier page load) is restored as-is.
 *
 * @param options - Options object.
 * @param options.imageByKey - `imageByKey` from the existing context selection.
 * @returns The initial `imageByKey`, `imageTransformByKey` and `croppedImageByKey`.
 */
function restoreImageState({
  imageByKey
}: {
  imageByKey: Record<string, File | null> | null | undefined;
}): RestoredImageState {
  const restored: RestoredImageState = { imageByKey: {}, imageTransformByKey: {}, croppedImageByKey: {} };
  if (!imageByKey) return restored;

  for (const [key, file] of Object.entries(imageByKey)) {
    // The rasterised card is output, not input: it was merged into the selection on the way out and
    // is redrawn from scratch on this mount. Restoring it would leave a stale picture of the card in
    // `imageByKey` that no element reads and that would be re-emitted even after the customer edits.
    if (key === UV_INSERT_CARD_PREVIEW_KEY) continue;
    // Narrow first and keep the narrowing: `croppedImageByKey` holds a `File`, never `File | null`.
    if (!(file instanceof File)) {
      restored.imageByKey[key] = file;
      continue;
    }
    const origin = getCroppedImageOrigin(file);
    if (!origin) {
      restored.imageByKey[key] = file;
      continue;
    }
    restored.imageByKey[key] = origin.source;
    restored.imageTransformByKey[key] = origin.transform;
    restored.croppedImageByKey[key] = { source: origin.source, transform: origin.transform, file };
  }

  return restored;
}

/**
 * Whether two pan/zoom transforms describe the same framing.
 *
 * @param a - The first transform.
 * @param b - The second transform.
 * @returns True when scale and both offsets match.
 */
function isSameTransform(a: ImageTransform, b: ImageTransform): boolean {
  return a.scale === b.scale && a.x === b.x && a.y === b.y;
}

/**
 * Whether two image maps hold the same files under the same keys.
 *
 * Compared by `File` identity rather than by map identity: `imageByKeyForCart` is rebuilt whenever
 * any of its inputs changes, including for a key the card does not draw, and a rebuilt map holding
 * the same photos is not a reason to withhold the picture drawn from them.
 *
 * @param a - One image map.
 * @param b - The other image map.
 * @returns True when both maps carry exactly the same keys and the same file for each.
 */
function isSameImageByKey(
  a: Record<string, File | null>,
  b: Record<string, File | null>
): boolean {
  if (a === b) return true;
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => Object.prototype.hasOwnProperty.call(b, key) && a[key] === b[key]);
}

/**
 * Whether one image key still needs rasterising: it holds an upload, the window size is known, and
 * no crop matching the current file and framing has landed yet.
 *
 * An unmoved upload counts: the window shows it `object-fit: cover`, so the original is wider or
 * taller than what the customer sees unless it happens to be the window's shape.
 *
 * Shared by the debounced crop effect (what to rasterise) and `isCropPending` (what the cart is
 * still waiting on) so the two can never disagree.
 *
 * @param options - Options object.
 * @param options.file - The file currently held for the key.
 * @param options.transform - The pan/zoom currently applied to it; undefined means unmoved.
 * @param options.windowSize - The visualiser window size, or undefined when it is not in pixels.
 * @param options.entry - The crop already produced for the key, if any.
 * @param options.failure - The crop that last failed for the key, if any.
 * @returns True when a crop is outstanding for this key.
 */
function isCropOutstanding({
  file,
  transform,
  windowSize,
  entry,
  failure
}: {
  file: File | null;
  transform: ImageTransform | undefined;
  windowSize: { width: number; height: number } | undefined;
  entry: CroppedImageEntry | undefined;
  failure: CropFailure | undefined;
}): boolean {
  if (!(file instanceof File)) return false;
  // A file we produced is already a crop: cropping it again would compound the upscale and produce
  // `...-cropped-cropped.jpg`. `restoreImageState` unwinds crops on remount so this should be
  // unreachable — it is kept as a hard guarantee that a crop is never cropped twice.
  if (isCroppedImage(file)) return false;
  if (windowSize == null) return false;
  const framing = transform ?? IDENTITY_IMAGE_TRANSFORM;
  // Settled, not outstanding: the same file and framing would fail the same way, so it waits for
  // the customer to re-frame or replace the photo rather than retrying in a loop.
  if (failure != null && failure.source === file && isSameTransform(failure.transform, framing)) return false;
  return entry == null || entry.source !== file || !isSameTransform(entry.transform, framing);
}

/**
 * Maps an ordered list of config handles to resolved UV insert config objects.
 * Preserves admin order and silently skips any handle not present in resolvedUvInserts.
 */
export function resolveAllowedUvInsertConfigs({
  handles,
  resolvedUvInserts
}: {
  handles: string[];
  resolvedUvInserts: ResolvedUvInserts | null | undefined;
}): UVInsertConfig[] {
  if (!resolvedUvInserts?.configs?.length || !handles.length) return [];
  const byHandle = new Map(resolvedUvInserts.configs.map((c) => [c.handle, c]));
  return handles.map((handle) => byHandle.get(handle)).filter((c): c is UVInsertConfig => c != null);
}

/**
 * Every `textByKey` key the host populates itself rather than the customer: each `TEXT_LIST`
 * element's month, year and moment keys, plus any static keyed TEXT marked `derived`.
 *
 * A `TEXT_LIST` row is wholly host-derived: the month from the stone, the year and moment from the
 * slots typed under that stone on the PAIRED step (`deriveStoryCardText`). So `setText` refuses all
 * three, the merge effect below owns them, and the seed restores them from `derivedTextByKey` rather
 * than from a prior selection — the slots are the only source, and the cart must agree with them.
 *
 * @param options - Options object.
 * @param options.config - The active UV insert config.
 * @returns The derived keys for the config, empty when there are none.
 */
function getDerivedTextKeys({ config }: { config: UVInsertConfig | null | undefined }): Set<string> {
  const keys = new Set<string>();
  if (!config) return keys;

  for (const face of config.faceElements) {
    for (const element of face.elementConfig) {
      if (isUvInsertTextListElement(element)) {
        for (const { monthKey, yearKey, momentKey } of getUvInsertTextListKeys({ element })) {
          keys.add(monthKey);
          if (yearKey) keys.add(yearKey);
          keys.add(momentKey);
        }
        continue;
      }
      if (!isUvInsertDerivedElement(element) || !isUvInsertUserConfigurableText(element)) continue;
      const key = element.lineItemPropertyKey?.trim();
      if (key) keys.add(key);
    }
  }

  return keys;
}

/**
 * The selection payload stored in `selections.slots[slotId].selected` for a UV_INSERT slot.
 * Pre-computes variantId and templateId so downstream cart code doesn't need resolvedUvInserts.
 */
export type UvInsertSlotSelection = {
  variantId: number;
  templateId: string | null;
  configHandle: string;
  /** Display title of the chosen theme variant — used in mini cart and summary. */
  themeTitle: string;
  textByKey: Record<string, string>;
  imageByKey: Record<string, File | null>;
};

type UsePersonalisationUvInsertSlotViewModelOptions = {
  /** Resolved UV insert data from PersonalisationContext. */
  resolvedUvInserts: ResolvedUvInserts | null;
  /** The config handle stored on the slot metaobject — used to find the correct UVInsertConfig. */
  configHandle: string | undefined;
  /**
   * Whether this slot is required (boolean or the string 'true' from Liquid/metaobject).
   * Required slots always opt in and never show the toggle.
   */
  isRequired: boolean | string | undefined;
  /**
   * Whether the toggle defaults to ON when the slot is optional (boolean or string 'true').
   * When true the insert is opted-in by default and the user must actively opt out.
   */
  uvInsertVisibleByDefault: boolean | string | undefined;
  /**
   * Whether the slot is free — if true, overrides the variant price to £0 with a strikethrough.
   * Matches the isFreeSlot pattern used by other slot types.
   */
  isFreeSlot: boolean | string | undefined;
  /**
   * Admin-configured fee override in pounds. When set, replaces the UV insert variant's
   * own product price for both the running total and the UI display.
   */
  overridePrice: number | null | undefined;
  /**
   * The existing selection from PersonalisationContext for this slot.
   * Populated when the user navigates back through the journey so the VM can restore its state.
   * Null means opted-out or no previous selection.
   */
  existingSelection: UvInsertSlotSelection | null | undefined;
  /**
   * Text the host derives from other slots (e.g. the Story of Us months and moments) rather than
   * the customer typing it. Merged into `textByKey` so it reaches the cart through the ordinary
   * line item property path; the customer cannot edit these keys.
   */
  derivedTextByKey?: Record<string, string>;
  /** Called with the selection when opted in, or null when opted out. */
  onSelectionChange: (selection: UvInsertSlotSelection | null) => void;
};

/**
 * ViewModel for PersonalisationUvInsertSlot.
 *
 * Manages all UV insert UI state (tab, theme, text inputs, image uploads) for a single
 * personalisation slot, and notifies the context via `onSelectionChange` on every meaningful change.
 *
 * Key opt-in logic:
 * - Required slots (`isRequired = true`): `optedIn` defaults to true and the toggle is hidden.
 *   The insert is always added to `_personalisation_choices`, even if blank.
 * - Optional, visible by default (`uvInsertVisibleByDefault = true`): toggle is shown and starts
 *   checked — the user must actively opt out.
 * - Optional, hidden by default (`uvInsertVisibleByDefault = false`): toggle is shown and starts
 *   unchecked — the user must actively opt in.
 */
export function usePersonalisationUvInsertSlotViewModel({
  resolvedUvInserts,
  configHandle,
  isRequired,
  uvInsertVisibleByDefault,
  isFreeSlot,
  overridePrice,
  existingSelection,
  derivedTextByKey = EMPTY_DERIVED_TEXT,
  onSelectionChange
}: UsePersonalisationUvInsertSlotViewModelOptions) {
  // Normalise the isRequired field which can arrive as boolean or the string 'true'.
  const isSlotRequired = isRequired === true || isRequired === 'true';

  // Normalise uvInsertVisibleByDefault — controls the toggle's default checked state.
  const isVisibleByDefault = uvInsertVisibleByDefault === true || uvInsertVisibleByDefault === 'true';

  // Restore optedIn from an existing context selection when navigating back.
  // A non-null existing selection means the user previously toggled on.
  // Falls back to isRequired / isVisibleByDefault when there is no prior selection.
  const [optedIn, setOptedIn] = useState(() => {
    if (existingSelection != null) return true;
    return isSlotRequired || isVisibleByDefault;
  });

  // Restore themeIndex from the previously selected variant ID when navigating back.
  const [themeIndex, setThemeIndex] = useState(() => {
    if (!existingSelection?.variantId || !resolvedUvInserts) return 0;
    const handleForRestore = existingSelection?.configHandle || configHandle;
    const config = resolvedUvInserts.configs.find((c) => c.handle === handleForRestore);
    if (!config) return 0;
    const idx = config.themes.findIndex((t) => Number(t.id) === existingSelection.variantId);
    return idx >= 0 ? idx : 0;
  });

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Restore text inputs from the previous selection.
  const [textByKey, setTextByKey] = useState<Record<string, string>>(() => existingSelection?.textByKey ?? {});

  // Restore image files from the previous selection.
  // File objects remain in memory as long as the context is mounted, so back-navigation
  // restores them correctly within the same page session. A crop in the selection is unwound to the
  // original upload plus its framing (see `restoreImageState`), so the customer can still zoom out.
  const [restoredImageState] = useState(() => restoreImageState({ imageByKey: existingSelection?.imageByKey }));
  const [imageByKey, setImageByKey] = useState<Record<string, File | null>>(restoredImageState.imageByKey);
  const [imageTransformByKey, setImageTransformByKey] = useState<Record<string, ImageTransform>>(
    restoredImageState.imageTransformByKey
  );
  // Rasterised versions of the uploads the customer has panned or zoomed. `imageByKey` keeps the
  // original so the visualiser preview stays instant; only the cart payload gets the crop.
  const [croppedImageByKey, setCroppedImageByKey] = useState<Record<string, CroppedImageEntry>>(
    restoredImageState.croppedImageByKey
  );
  // Crops that could not be produced. Only the latest attempt per key is kept, and it only counts
  // while the key still holds the file and framing it was for (see `cropFailedKeys`).
  const [failedCropByKey, setFailedCropByKey] = useState<Record<string, CropFailure>>({});
  // The most recent successful rasterisation of the assembled card, tagged with the images it was
  // drawn from so the merge below can tell a stale picture from a current one. Not restored from the
  // selection: it is redrawn from the restored text and photos on mount (see `restoreImageState`).
  const [cardPreview, setCardPreview] = useState<CardPreviewState | null>(null);
  const [themeChooserOpen, setThemeChooserOpen] = useState(false);
  const [suggestionChooserOpen, setSuggestionChooserOpen] = useState(false);
  const [selectedSuggestionGroupIndex, setSelectedSuggestionGroupIndex] = useState(0);

  const previousConfigHandleRef = useRef<string | null>(null);
  const hasRestoredFromExistingRef = useRef(false);
  /** What the current `cardPreview` was drawn from, so an unchanged card is not redrawn. */
  const cardPreviewInputsRef = useRef<{
    textByKey: Record<string, string>;
    imageByKey: Record<string, File | null>;
    variantImageUrl: string | null;
  } | null>(null);

  // Hold onSelectionChange in a ref so effects don't need to re-subscribe when the callback identity changes.
  const onSelectionChangeRef = useRef(onSelectionChange);
  useEffect(() => {
    onSelectionChangeRef.current = onSelectionChange;
  }, [onSelectionChange]);

  // Find the config matching this slot's configured handle.
  const activeConfig: UVInsertConfig | undefined = useMemo(
    () => resolvedUvInserts?.configs.find((c) => c.handle === configHandle),
    [resolvedUvInserts, configHandle]
  );

  // Reset to first tab and first theme whenever the active config changes (e.g. user switches insert type).
  useEffect(() => {
    setActiveTabIndex(0);
    setThemeIndex(0);
  }, [activeConfig?.id]);

  // Reset suggestion group index when the tab changes.
  useEffect(() => {
    setSelectedSuggestionGroupIndex(0);
  }, [activeTabIndex]);

  // Keys the host owns — never seeded from a prior selection and never customer-editable.
  const derivedTextKeys = useMemo(() => getDerivedTextKeys({ config: activeConfig }), [activeConfig]);

  // Drops stale keys from previous insert types.
  // On insert-type switch: reset to empty defaults. On back-navigation: restore once from context.
  useEffect(() => {
    if (!activeConfig || !configHandle) return;

    const isConfigSwitch = previousConfigHandleRef.current != null && previousConfigHandleRef.current !== configHandle;

    setTextByKey((prev) => {
      let source = prev;
      if (isConfigSwitch) {
        source = {};
      } else if (
        !hasRestoredFromExistingRef.current &&
        existingSelection?.configHandle === configHandle &&
        existingSelection.textByKey
      ) {
        hasRestoredFromExistingRef.current = true;
        source = existingSelection.textByKey;
      }

      const next: Record<string, string> = {};
      for (const face of activeConfig.faceElements) {
        for (const el of face.elementConfig) {
          // TEXT_LIST has no key of its own — one month/year/moment key per row instead, all three
          // derived from the PAIRED step's slots, so they are seeded from the derive and never
          // from `source` (a prior selection's year or moment may no longer have a slot behind it).
          if (isUvInsertTextListElement(el)) {
            for (const { monthKey, yearKey, momentKey } of getUvInsertTextListKeys({ element: el })) {
              for (const key of [monthKey, yearKey, momentKey]) {
                if (key) next[key] = derivedTextByKey[key] ?? '';
              }
            }
            continue;
          }
          if (isUvInsertUserConfigurableText(el) && el.lineItemPropertyKey) {
            const key = el.lineItemPropertyKey;
            // Derived keys come from the host, not from the customer's prior selection.
            if (derivedTextKeys.has(key)) {
              next[key] = derivedTextByKey[key] ?? '';
              continue;
            }
            next[key] = source[key] ?? (el.type === 'TEXT' && el.editable === false ? (el.value ?? '') : '');
          }
        }
      }
      return next;
    });

    if (isConfigSwitch) {
      setImageByKey({});
      setImageTransformByKey({});
      setCroppedImageByKey({});
      setFailedCropByKey({});
      // Block restore from stale context selection on the next effect run.
      hasRestoredFromExistingRef.current = true;
    }

    previousConfigHandleRef.current = configHandle;
    // derivedTextByKey is read as the initial seed only — the merge effect below owns every
    // later change, so re-running this reset on each derive would be redundant work.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConfig, configHandle, existingSelection?.configHandle, derivedTextKeys]);

  // Fold host-derived text into textByKey whenever it changes (e.g. the customer picks a stone
  // or edits a moment on the PAIRED step). Only derived keys are touched, and an unchanged map
  // returns the previous state so the notify effect below does not re-fire.
  useEffect(() => {
    if (derivedTextKeys.size === 0) return;

    setTextByKey((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of derivedTextKeys) {
        const value = derivedTextByKey[key] ?? '';
        if (next[key] !== value) {
          next[key] = value;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [derivedTextByKey, derivedTextKeys]);

  // Declared above the crop and preview effects because the card preview draws the selected variant
  // image as its FRONT background, mirroring `DevFaceVisualisers`.
  const theme = activeConfig?.themes[themeIndex];
  const selectedThemeImageSrc = theme ? getLiquidProductVariantImageSrc({ variant: theme }) : null;

  // Whether this config crops framed uploads at all — see `UV_INSERT_CROP_TO_FRAME_HANDLES`.
  const cropsToFrame = configHandle != null && UV_INSERT_CROP_TO_FRAME_HANDLES.includes(configHandle);

  // Whether the editable face belongs in the piece visualiser's carousel rather than in the step
  // body. Only the keepsake card, which was designed for the space saving; every other insert keeps
  // its face where it has always been.
  const rendersInCarousel = configHandle != null && UV_INSERT_CARD_SLIDE_HANDLES.includes(configHandle);

  // Pixel size of each IMAGE_UPLOAD window — the container the crop geometry is expressed against.
  // Left empty for a config that does not crop, which is the single switch the whole crop path hangs
  // off: `isCropOutstanding` needs a window size, so with none the crop effect never fires,
  // `croppedImageByKey` stays empty, `imageByKeyForCart` is `imageByKey`, and `isCropPending` is false.
  const imageWindowSizes = useMemo(
    () => (cropsToFrame ? getImageWindowSizes({ config: activeConfig }) : NO_IMAGE_WINDOW_SIZES),
    [activeConfig, cropsToFrame]
  );

  // Rasterise what the customer framed, once their pan/zoom has settled. An unmoved upload is
  // cropped too, to the centre the window shows: the print must match the card on screen.
  useEffect(() => {
    const pending = Object.entries(imageByKey).filter(([key, file]) =>
      isCropOutstanding({
        file,
        transform: imageTransformByKey[key],
        windowSize: imageWindowSizes[key],
        entry: croppedImageByKey[key],
        failure: failedCropByKey[key]
      })
    );
    if (pending.length === 0) return undefined;

    let cancelled = false;
    const timer = setTimeout(() => {
      for (const [key, file] of pending) {
        const transform = imageTransformByKey[key] ?? IDENTITY_IMAGE_TRANSFORM;
        const frame = imageWindowSizes[key];
        void cropImageToView({
          file: file as File,
          containerWidth: frame.width,
          containerHeight: frame.height,
          transform
        })
          // It resolves rather than rejects, but a rejection is the same failure as its fallback.
          .catch(() => file as File)
          .then((cropped) => {
            if (cancelled) return;
            // Handed back the upload itself: it could not crop (no canvas, a photo the browser cannot
            // decode). That file is not what the customer framed, so it is never recorded as the
            // crop; the failure is, and the slot shows an error and holds add to bag until a re-frame
            // or a new photo crops.
            if (cropped === file) {
              setFailedCropByKey((prev) => ({ ...prev, [key]: { source: file as File, transform } }));
              return;
            }
            // Record where the crop came from before it leaves for the cart: the selection carries
            // only the crop, so this registry is the only route back to the original on a remount.
            rememberCroppedImage({ crop: cropped, source: file as File, transform });
            setCroppedImageByKey((prev) => ({ ...prev, [key]: { source: file as File, transform, file: cropped } }));
          });
      }
    }, CROP_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [imageByKey, imageTransformByKey, imageWindowSizes, croppedImageByKey, failedCropByKey]);

  /**
   * Image keys whose crop failed for the file and framing they hold now (ALBAU-865).
   *
   * The cart gets no photo for them rather than the uncropped upload, the slot shows an error under
   * the upload and holds add to bag, and the customer's re-frame or new photo clears them simply
   * by no longer matching. The preview keeps the photo (`imageByKey`) so it can be re-framed.
   */
  const cropFailedKeys = useMemo(
    () =>
      Object.entries(failedCropByKey)
        .filter(
          ([key, failure]) =>
            imageByKey[key] === failure.source &&
            isSameTransform(imageTransformByKey[key] ?? IDENTITY_IMAGE_TRANSFORM, failure.transform)
        )
        .map(([key]) => key),
    [failedCropByKey, imageByKey, imageTransformByKey]
  );

  // What the cart receives: the original upload, replaced by its crop wherever one matches the
  // current file and framing. A crop left over from an earlier framing is ignored rather than sent.
  // A failed crop sends no photo at all: never the uncropped one.
  const imageByKeyForCart = useMemo(() => {
    let next: Record<string, File | null> | null = null;
    for (const [key, entry] of Object.entries(croppedImageByKey)) {
      const current = imageByKey[key];
      if (current !== entry.source) continue;
      if (!isSameTransform(imageTransformByKey[key] ?? { scale: 1, x: 0, y: 0 }, entry.transform)) continue;
      next = next ?? { ...imageByKey };
      next[key] = entry.file;
    }
    for (const key of cropFailedKeys) {
      next = next ?? { ...imageByKey };
      next[key] = null;
    }
    return next ?? imageByKey;
  }, [imageByKey, imageTransformByKey, croppedImageByKey, cropFailedKeys]);

  /**
   * True while an upload has been framed but its crop has not landed yet.
   *
   * The race this names: the crop starts `CROP_DEBOUNCE_MS` after the pan/zoom settles and then
   * decodes and re-encodes on the main thread, which is seconds for a large phone photo on
   * mid-range hardware. Throughout that window `imageByKeyForCart` still holds the *uncropped*
   * upload, so a customer who frames the polaroid and immediately advances has the unframed photo
   * sent to the cart and printed, with no signal to them or to us.
   *
   * The slot publishes this to `PersonalisationContext.cropPendingSlotIds`, and the footer
   * disables its primary button and holds add to bag until it clears. It is deliberately NOT in
   * `stepValidation.js`: validation reads only `selection.selected`, a `UvInsertSlotSelection`,
   * whose shape is a hard cart contract that must not gain a field.
   *
   * A failed crop is settled, so not pending; the slot holds the footer for it through
   * `cropFailedKeys` instead.
   */
  const isCropPending = useMemo(
    () =>
      Object.entries(imageByKey).some(([key, file]) =>
        isCropOutstanding({
          file,
          transform: imageTransformByKey[key],
          windowSize: imageWindowSizes[key],
          entry: croppedImageByKey[key],
          failure: failedCropByKey[key]
        })
      ),
    [imageByKey, imageTransformByKey, imageWindowSizes, croppedImageByKey, failedCropByKey]
  );

  // Whether this config's assembled face is rasterised for the cart — see
  // `UV_INSERT_CARD_PREVIEW_HANDLES`. False leaves every map below identical to the crop path's
  // output, so a non-allowlisted insert behaves exactly as it did before this existed.
  const rendersCardPreview = configHandle != null && UV_INSERT_CARD_PREVIEW_HANDLES.includes(configHandle);

  // Drop a preview drawn for a different insert the moment the config changes: it is a picture of a
  // card the customer is no longer building.
  useEffect(() => {
    setCardPreview(null);
    cardPreviewInputsRef.current = null;
  }, [activeConfig?.id]);

  // Redraw the card once the customer's edits have settled.
  //
  // Fed from `imageByKeyForCart`, not `imageByKey`, so the polaroid in the picture is the crop that
  // will actually print rather than the untouched upload. It deliberately does not depend on
  // `cardPreview`, which only the merged map below reads — so drawing cannot re-trigger itself.
  useEffect(() => {
    if (!rendersCardPreview || !optedIn || !activeConfig) return undefined;
    if (!hasCardPreviewContent({ textByKey, imageByKey: imageByKeyForCart })) return undefined;

    const drawn = cardPreviewInputsRef.current;
    if (
      drawn != null &&
      drawn.textByKey === textByKey &&
      drawn.imageByKey === imageByKeyForCart &&
      drawn.variantImageUrl === selectedThemeImageSrc
    ) {
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(() => {
      void renderCardPreview({
        config: activeConfig,
        textByKey,
        imageByKey: imageByKeyForCart,
        variantImageUrl: selectedThemeImageSrc
      }).then((file) => {
        // A failed render leaves the ref untouched, so the next edit tries again rather than
        // treating the failure as the card's current state.
        if (cancelled || !file) return;
        cardPreviewInputsRef.current = { textByKey, imageByKey: imageByKeyForCart, variantImageUrl: selectedThemeImageSrc };
        setCardPreview({ file, imageByKey: imageByKeyForCart, variantImageUrl: selectedThemeImageSrc });
      }).catch((error) => {
        // `renderCardPreview` returns null rather than rejecting, so this is belt and braces — but an
        // unhandled rejection here would surface to the customer as a page error over a picture.
        console.warn('Card preview could not be drawn', error);
      });
    }, CARD_PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [rendersCardPreview, optedIn, activeConfig, textByKey, imageByKeyForCart, selectedThemeImageSrc]);

  /**
   * What the cart actually receives: the crop-substituted uploads, plus the rasterised card.
   *
   * `createPayload` spreads every `File` in here into the base line's properties as multipart form
   * data, so Shopify hosts the image and the property becomes a CDN URL that `cart-item-row.liquid`
   * renders as a thumbnail. Nothing in the cart, gateway or Liquid needed changing for that.
   *
   * Staleness is tolerated for text but not for photos, which is where the two kinds of drift
   * differ in what they cost:
   *
   * - **Text out of date**: emitted anyway. The print is driven by the text line item properties, so
   *   the picture is only a representation of them and a caption one keystroke behind is a blemish.
   *   Withholding it would mean a customer who hits add to bag inside the debounce window gets no
   *   picture at all, which is the worse failure.
   * - **Photo out of date**: withheld. Between the customer replacing the polaroid and the redraw
   *   landing (the debounce plus a main-thread render, longer on a phone), the last successful
   *   render still shows the *previous* photo while `UVFrontImage` already carries the new one.
   *   Support looking at that order would see two different photos on one line and have no way to
   *   tell which the customer meant. No picture is better than a contradictory one.
   *
   * The picture comes back the moment the redraw lands, which is why the images it was drawn from
   * live in state next to it rather than in `cardPreviewInputsRef` alone.
   */
  const imageByKeyForCartWithPreview = useMemo(() => {
    if (!rendersCardPreview || !cardPreview) return imageByKeyForCart;
    // A card the customer has emptied out again should not keep showing its last picture.
    if (!hasCardPreviewContent({ textByKey, imageByKey: imageByKeyForCart })) return imageByKeyForCart;
    if (!isSameImageByKey(cardPreview.imageByKey, imageByKeyForCart)) return imageByKeyForCart;
    if (cardPreview.variantImageUrl !== selectedThemeImageSrc) return imageByKeyForCart;
    return { ...imageByKeyForCart, [UV_INSERT_CARD_PREVIEW_KEY]: cardPreview.file };
  }, [rendersCardPreview, cardPreview, imageByKeyForCart, textByKey, selectedThemeImageSrc]);

  // Notify the context whenever selection state changes.
  // When opted out, clears the selection (null) so the insert is excluded from choices.
  // When opted in, sends the full selection so the insert is included (even if blank).
  useEffect(() => {
    if (!activeConfig || !configHandle) return;

    if (!optedIn) {
      onSelectionChangeRef.current(null);
      return;
    }

    const variantId = Number(activeConfig.themes[themeIndex]?.id ?? 0);
    if (!variantId) return;
    const templateId = resolvedUvInserts?.templateIds[variantId] ?? null;
    const themeTitle = String(activeConfig.themes[themeIndex]?.title ?? '');
    onSelectionChangeRef.current({
      variantId,
      templateId,
      configHandle,
      themeTitle,
      textByKey,
      imageByKey: imageByKeyForCartWithPreview
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeIndex, textByKey, imageByKeyForCartWithPreview, activeConfig, configHandle, resolvedUvInserts, optedIn]);

  const faceTabItems = useMemo(
    () => (activeConfig ? getUvInsertFaceTabItems({ faceElements: activeConfig.faceElements }) : []),
    [activeConfig]
  );

  const facesToShow = useMemo(
    () =>
      activeConfig
        ? getFaceElementsForRegionTab({ faceElements: activeConfig.faceElements, tabIndex: activeTabIndex })
        : [],
    [activeConfig, activeTabIndex]
  );

  const imageUploadKeysForActiveTab = useMemo(() => {
    if (!activeConfig) return [] as string[];
    return getImageUploadPropertyKeysForRegionTab({
      faceElements: activeConfig.faceElements,
      tabIndex: activeTabIndex
    });
  }, [activeConfig, activeTabIndex]);

  const firstTextKeyOnActiveTab = useMemo(() => {
    if (!activeConfig) return null;
    return getSuggestionTargetTextPropertyKeyForRegionTab({
      faceElements: activeConfig.faceElements,
      tabIndex: activeTabIndex
    });
  }, [activeConfig, activeTabIndex]);

  // Price of the currently selected theme variant (in pounds), converted from the Liquid pence value.
  const variantPricePounds = useMemo(() => {
    if (!activeConfig) return null;
    const raw = activeConfig.themes[themeIndex]?.price;
    if (raw == null) return null;
    const pence = parseFloat(String(raw));
    return Number.isNaN(pence) ? null : pence / 100;
  }, [activeConfig, themeIndex]);

  /**
   * The price to display in the slot UI and add to the running total.
   * Priority: isFreeSlot (→ 0) > overridePrice > variant price from Liquid config.
   */
  const effectivePrice = useMemo((): number | null => {
    if (isFreeSlot === true || isFreeSlot === 'true') return 0;
    if (typeof overridePrice === 'number') return overridePrice;
    return variantPricePounds;
  }, [isFreeSlot, overridePrice, variantPricePounds]);

  /**
   * The original variant price — only populated when isFreeSlot is true, for strikethrough display.
   */
  const originalPrice = useMemo((): number | null => {
    if (isFreeSlot !== true && isFreeSlot !== 'true') return null;
    return variantPricePounds;
  }, [isFreeSlot, variantPricePounds]);

  const setText = useCallback(
    (key: string, value: string) => {
      // Derived keys are owned by the host — a stray edit would be overwritten anyway.
      if (derivedTextKeys.has(key)) return;
      setTextByKey((prev) => ({ ...prev, [key]: value }));
    },
    [derivedTextKeys]
  );

  const onSuggestionPick = useCallback(
    (text: string) => {
      if (!firstTextKeyOnActiveTab || derivedTextKeys.has(firstTextKeyOnActiveTab)) return;
      setTextByKey((prev) => ({ ...prev, [firstTextKeyOnActiveTab]: text }));
    },
    [derivedTextKeys, firstTextKeyOnActiveTab]
  );

  return {
    isSlotRequired,
    optedIn,
    setOptedIn,
    activeConfig,
    effectivePrice,
    originalPrice,
    themeIndex,
    setThemeIndex,
    activeTabIndex,
    setActiveTabIndex,
    theme,
    selectedThemeImageSrc,
    faceTabItems,
    facesToShow,
    firstTextKeyOnActiveTab,
    rendersInCarousel,
    textByKey,
    setText,
    imageByKey,
    setImageByKey,
    imageTransformByKey,
    setImageTransformByKey,
    isCropPending,
    cropFailedKeys,
    imageUploadKeysForActiveTab,
    themeChooserOpen,
    setThemeChooserOpen,
    suggestionChooserOpen,
    setSuggestionChooserOpen,
    selectedSuggestionGroupIndex,
    setSelectedSuggestionGroupIndex,
    onSuggestionPick
  };
}
